'use client';
import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { imageUrl } from '@/lib/medusa';
import { isNoShop, getProductPriceInfo } from '@/lib/data/products';
import PriceTag from '@/components/PriceTag';
import { useTranslation } from '@/lib/i18n/useTranslation';

const DEFAULT_IMAGE = '/images/default-product-image.jpg';
const TWEEN_FACTOR = 0.30;

// Embla can only build a seamless infinite loop when the slides comfortably
// exceed the viewport. On the widest layout a slide is 20% (5 per view), so we
// aim for ~2 viewports of slides before looping is reliable. Categories with
// fewer products (e.g. "Schalen") get their slides repeated to reach this count.
const MIN_LOOP_SLIDES = 10;

const EMBLA_OPTIONS = {
    dragFree: true,
    align: 'center',
    slidesToScroll: 1,
    containScroll: 'keepSnaps',
    duration: 80,
};

// stopOnInteraction:false keeps the carousel auto-rotating after a drag;
// stopOnMouseEnter pauses it while the visitor is inspecting a piece.
const AUTOPLAY_OPTIONS = {
    delay: 4000,
    stopOnInteraction: false,
    stopOnMouseEnter: true,
};

const numberWithinRange = (number, min, max) => Math.min(Math.max(number, min), max);

// Each slide reveals itself only once its image has actually decoded. This
// removes the previous flicker where the reveal animation played on an empty
// frame and the bitmap popped in afterwards. A muted skeleton fills the space
// until then, and the reveal is a compositor-only opacity/transform transition
// (GPU-friendly, respects prefers-reduced-motion).
const CarouselSlide = React.memo(function CarouselSlide({
    product,
    index,
    selectedCategory,
    soldLabel,
    onSelect,
    locale,
    promotions,
}) {
    const [loaded, setLoaded] = useState(false);

    const showPrice = selectedCategory === 'shop' && !isNoShop(product);
    const priceInfo = showPrice ? getProductPriceInfo(product, promotions) : null;
    const stock = product.variants?.[0]?.inventory_quantity ?? null;
    const thumb = product.thumbnail || product.images?.[0]?.url;
    const src = thumb ? imageUrl(thumb) : DEFAULT_IMAGE;

    // Covers browser-cached images that may already be complete before the
    // onLoad handler is attached (otherwise they'd stay hidden).
    const captureRef = useCallback((node) => {
        if (node && node.complete && node.naturalWidth > 0) setLoaded(true);
    }, []);

    return (
        <div
            className="embla__slide cursor-pointer group h-full relative"
            onClick={onSelect}
        >
            <div className="slide-content flex flex-col h-full">
                <div className="text-content px-1 pt-1 mb-1">
                    <h2 className="text-lg font-thin group-hover:-translate-y-2 transition-transform duration-300 ease-in-out">
                        {product.title}
                    </h2>
                    <div className="flex justify-between items-center group-hover:-translate-y-2 transition-transform duration-300 ease-in-out">
                        <p className="text-md font-thin flex-grow pr-2 truncate overflow-hidden">
                            {product.subtitle || product.description}
                        </p>
                        {priceInfo?.amount != null && (
                            <p className="text-md font-thin whitespace-nowrap ml-2">
                                <PriceTag info={priceInfo} locale={locale} showBadge />
                            </p>
                        )}
                    </div>
                </div>
                <div className={`image-container ${loaded ? '' : 'bg-muted animate-pulse'}`}>
                    <Image
                        ref={captureRef}
                        src={src}
                        alt={product.title}
                        className={`slide-image w-full h-full object-cover transform-gpu transition duration-700 ease-out motion-reduce:transition-none motion-reduce:opacity-100 motion-reduce:translate-y-0 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3'}`}
                        width={500}
                        height={370}
                        priority={index === 0}
                        loading={index === 0 ? 'eager' : 'lazy'}
                        sizes="(max-width: 480px) 95vw, (max-width: 768px) 50vw, (max-width: 992px) 33vw, (max-width: 1200px) 25vw, 20vw"
                        onLoad={() => setLoaded(true)}
                        onError={() => setLoaded(true)}
                    />
                </div>
                {stock === 0 && (
                    <div className="absolute top-2 right-2 bg-gray-300 text-black px-2 py-1 text-xs font-pp-neue font-thin group-hover:-translate-y-2 transition-transform duration-300 ease-in-out">
                        {soldLabel}
                    </div>
                )}
            </div>
        </div>
    );
});

const ImageCarousel = ({ selectedCategory, onCursorOverCarousel, products = [], promotions = [] }) => {
    const { t, locale } = useTranslation();

    // Repeat the products of small categories so the looped track always has
    // enough slides to render without gaps. Click-through still resolves the
    // real product via its handle, so duplicates are harmless.
    const slides = useMemo(() => {
        if (products.length <= 1 || products.length >= MIN_LOOP_SLIDES) return products;
        const repeat = Math.ceil(MIN_LOOP_SLIDES / products.length);
        return Array.from({ length: repeat }).flatMap(() => products);
    }, [products]);

    // A single product (or empty category) can't loop, so fall back to a
    // static, centered slide instead of a broken/empty carousel.
    const loopEnabled = slides.length > 1;

    const [emblaRef, emblaApi] = useEmblaCarousel(
        { ...EMBLA_OPTIONS, loop: loopEnabled },
        [Autoplay(AUTOPLAY_OPTIONS)]
    );

    const containerRef = useRef(null);
    const router = useRouter();
    const tweenFactor = useRef(0);
    const tweenNodes = useRef([]);

    const setTweenNodes = useCallback((emblaApi) => {
        tweenNodes.current = emblaApi.slideNodes().map((slideNode) => {
            return slideNode.querySelector('.slide-content');
        });
    }, []);

    const setTweenFactor = useCallback((emblaApi) => {
        tweenFactor.current = TWEEN_FACTOR * emblaApi.scrollSnapList().length;
    }, []);

    const tweenScale = useCallback((emblaApi, eventName) => {
        if (!emblaApi) return;
        const engine = emblaApi.internalEngine();
        const scrollProgress = emblaApi.scrollProgress();
        const slidesInView = emblaApi.slidesInView();
        const isScrollEvent = eventName === 'scroll';

        emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
            let diffToTarget = scrollSnap - scrollProgress;
            const slidesInSnap = engine.slideRegistry[snapIndex];

            slidesInSnap.forEach((slideIndex) => {
                if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

                if (engine.options.loop) {
                    engine.slideLooper.loopPoints.forEach((loopItem) => {
                        const target = loopItem.target();
                        if (slideIndex === loopItem.index && target !== 0) {
                            const sign = Math.sign(target);
                            if (sign === -1) diffToTarget = scrollSnap - (1 + scrollProgress);
                            if (sign === 1) diffToTarget = scrollSnap + (1 - scrollProgress);
                        }
                    });
                }

                const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor.current);
                const scale = numberWithinRange(tweenValue, 0.7, 1).toString();
                const tweenNode = tweenNodes.current[slideIndex];
                if (tweenNode) {
                    tweenNode.style.transform = `scale(${scale})`;

                    const opacity = numberWithinRange(tweenValue, 0.7, 1).toString();
                    const textContent = tweenNode.querySelector('.text-content');
                    if (textContent) {
                        textContent.style.opacity = opacity;
                    }
                }
            });
        });
    }, []);

    const applyScalingEffect = useCallback(() => {
        if (!emblaApi) return;
        setTweenNodes(emblaApi);
        setTweenFactor(emblaApi);
        tweenScale(emblaApi);
    }, [emblaApi, setTweenNodes, setTweenFactor, tweenScale]);

    useEffect(() => {
        if (!emblaApi) return;

        applyScalingEffect();

        // Embla invokes listeners with (emblaApi, eventName); passing tweenScale
        // directly keeps the on/off references identical so the listener is
        // actually removed on cleanup (previously it leaked on every reInit).
        emblaApi.on('reInit', applyScalingEffect).on('scroll', tweenScale);

        return () => {
            emblaApi.off('reInit', applyScalingEffect).off('scroll', tweenScale);
        };
    }, [emblaApi, applyScalingEffect, tweenScale]);

    // Re-initialise whenever the rendered slides change (async product load or
    // category switch) so Embla picks up the new slide count and loop setting.
    useEffect(() => {
        if (!emblaApi) return;
        emblaApi.reInit({ ...EMBLA_OPTIONS, loop: loopEnabled });
        applyScalingEffect();
    }, [emblaApi, slides, loopEnabled, applyScalingEffect]);

    const handleSlideClick = useCallback((product) => {
        try {
            localStorage.setItem('lastSelectedCategory', selectedCategory);
        } catch {}
        router.push(`/product/${product.handle}`);
    }, [router, selectedCategory]);

    const handleMouseEnter = useCallback(() => {
        onCursorOverCarousel(true);
    }, [onCursorOverCarousel]);

    const handleMouseLeave = useCallback(() => {
        onCursorOverCarousel(false);
    }, [onCursorOverCarousel]);

    const soldLabel = t('home.sold', 'Sold');

    const carouselContent = useMemo(() => (
        slides.map((product, index) => (
            <CarouselSlide
                key={`${product.id}-${index}`}
                product={product}
                index={index}
                selectedCategory={selectedCategory}
                soldLabel={soldLabel}
                onSelect={() => handleSlideClick(product)}
                locale={locale}
                promotions={promotions}
            />
        ))
    ), [slides, selectedCategory, handleSlideClick, soldLabel, locale, promotions]);

    return (
        <div
            className="embla"
            ref={containerRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="embla__viewport h-full" ref={emblaRef}>
                <div className="embla__container h-full">
                    {carouselContent}
                </div>
            </div>
        </div>
    );
};

export default React.memo(ImageCarousel);
